<template>
  <transition name="node-info-layer">
    <div v-if="visible"
         :data-id="id"
         class="node-info-layer"
         :style="{ left: (left * reversedPaneScale) + 'px', top: (top * reversedPaneScale) + 'px', transform: `scale(${reversedPaneScale}) translateZ(0)` }"
         @mousedown="stopPropagation"
         @touchstart="stopPropagation">
      <div class="info-item">
        <span class="sequence">
          <strong>{{ focusedSequenceIndex + 1 }}</strong>
          Sequence
        </span>
        <span v-if="grouping"
              ref="fromGroupName"
              class="group-info"
              :style="{ '--group-color': fromGroup.color }">
          {{ fromGroup.name }}
        </span>
        <span class="url">
          {{ fromUrl || $t('trend.other') }}
        </span>
      </div>
      <div class="info-item">
        <span class="sequence">
          <strong>{{ focusedSequenceIndex + 2 }}</strong>
          Sequence
        </span>
        <span v-if="grouping"
              ref="toGroupName"
              class="group-info"
              :style="{ '--group-color': toGroup.color }">
          {{ toGroup.name }}
        </span>
        <span class="url">
          {{ toUrl || $t('trend.other') }}
        </span>
      </div>
      <div class="summary"
           :class="{ 'thru-path': thruData }">
        <strong class="sessions">
          <i class="icon icon--ba-session"></i>
          {{ getComputedThruSessCnt | letterThousandMillion(0) }}
          <span class="percent">({{ thruData ? isThruPath ? thruPercentOfAllSessions : '0.0' : percentOfAllSessions | fixedInt(2, 1) }}%)</span>
          <span v-if="thruData"
                class="total">
            / {{ getComputedSess | letterThousandMillion(0) }}
            <span class="percent">({{ percentOfAllSessions }}%)</span>
          </span>
        </strong>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex'
import { onlySubdomain } from '@/libs/utils'

export default {
  name: 'BaNodeInfoLayer',
  props: {
    left: {
      type: Number,
      required: true
    },
    top: {
      type: Number,
      required: true
    },
    nodesDataArray: {
      type: Array,
      required: true
    },
    thruData: {
      type: Object
    },
    nodeMap: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      required: true
    },
    grouping: {
      type: Boolean,
      required: true
    },
    group: {
      type: String,
      required: true
    },
    paneScale: {
      type: Number,
      required: true
    },
    focusedSequenceIndex: {
      type: Number,
      required: true
    },
    focusedNodeIndex: {
      type: Number,
      required: true
    },
    focusedNextIndex: {
      type: Number,
      required: true
    },
    isThruPath: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
    }
  },
  watch: {
    left (value) {
      requestAnimationFrame(() => {
        this.$el.style.marginLeft = this.$el.style.marginTop = ''
        const rect = this.$el.getBoundingClientRect()
        this.$el.style.marginLeft = rect.right > window.innerWidth ? -(rect.width + 5) * this.reversedPaneScale + 'px' : ''
        this.$el.style.marginTop = rect.bottom > window.innerHeight ? -(rect.height + 5) * this.reversedPaneScale + 'px' : ''
      })
    }
  },
  computed: {
    ...mapState([
      'journeyOptions',
      'domain',
      'originTrendData',
      'isSampling'
    ]),
    getComputedThruSessCnt () {
      const sess = this.thruData ? this.isThruPath ? (this.thruPathInfo.sessCnt || 0) : 0 : this.pathInfo.sessCnt
      return this.isSamplingOptions ? sess / this.originTrendData.sessionSamplingRate : sess
    },
    getComputedSess () {
      const sess = this.pathInfo.sessCnt
      return this.isSamplingOptions ? sess / this.originTrendData.sessionSamplingRate : sess
    },
    isSamplingOptions () {
      return this.isSampling && (!this.journeyOptions.survey || this.journeyOptions.survey === {})
    },
    id () {
      return this.focusedSequenceIndex + '-' + this.focusedNodeIndex + '-' + this.focusedNextIndex
    },
    fromId () {
      return this.nodesDataArray[this.focusedSequenceIndex].nodesArray[this.focusedNodeIndex]._id
    },
    toId () {
      return this.nodesDataArray[this.focusedSequenceIndex + 1].nodesArray[this.focusedNextIndex]._id
    },
    fromUrl () {
      return this.nodeMap[this.fromId] ? this.simplifyUrl(this.nodeMap[this.fromId].url || '') : ''
    },
    toUrl () {
      return this.nodeMap[this.toId] ? this.simplifyUrl(this.nodeMap[this.toId].url || '') : ''
    },
    fromGroup () {
      return this.getGroupInfo(this.fromId)
    },
    toGroup () {
      return this.getGroupInfo(this.toId)
    },
    pathInfo () {
      return this.nodesDataArray[this.focusedSequenceIndex].nodesArray[this.focusedNodeIndex].next[this.toId]
    },
    percentOfAllSessions () {
      const nodeData = this.nodesDataArray[this.focusedSequenceIndex].nodesArray[this.focusedNodeIndex]
      return (nodeData.next[this.toId].sessCnt / nodeData.sessCnt) * 100
    },
    thruPathInfo () {
      const nodeData = this.nodesDataArray[this.focusedSequenceIndex].nodesArray[this.focusedNodeIndex]
      const nextData = nodeData.next[this.toId]
      return this.thruData.seqMap[this.focusedSequenceIndex].nodes[nodeData._id].next[nextData._id] || {}
    },
    thruPercentOfAllSessions () {
      const nodeData = this.nodesDataArray[this.focusedSequenceIndex].nodesArray[this.focusedNodeIndex]
      return ((this.thruPathInfo.sessCnt || 0) / nodeData.sessCnt) * 100
    },
    reversedPaneScale () {
      return 1 / this.paneScale
    }
  },
  mounted () {
    this.syncGroupHeight()
    document.body.addEventListener('pointerdown', this.checkCloseLayer)
  },
  updated () {
    this.syncGroupHeight()
  },
  beforeDestroy () {
    document.body.removeEventListener('pointerdown', this.checkCloseLayer)
  },
  methods: {
    simplifyUrl (url) {
      return url ? url.split('://')[1].split('?')[0] : ''
    },
    checkCloseLayer (e) {
      if (this.visible) {
        const body = document.body
        let target = e.target
        while (target !== body) {
          if (target.dataset.id === this.id || target.id.indexOf('-' + this.id) > -1) {
            return
          }
          target = target.parentNode
        }
        this.$emit('onCloseInfoLayer')
      }
    },
    getGroupInfo (id) {
      const nodeInfo = this.nodeMap[id]
      if (nodeInfo) {
        return {
          name: onlySubdomain(nodeInfo.tagInfo[this.group].tagName || this.$t('group.upcoming'), this.journeyOptions.domain),
          color: nodeInfo.tagInfo && nodeInfo.tagInfo[this.group] ? nodeInfo.tagInfo[this.group].colorHex || '#bbb' : '#bbb'
        }
      } else {
        return {
          name: this.$t('trend.other'),
          color: '#666'
        }
      }
    },
    syncGroupHeight () {
      if (this.grouping && this.$refs.fromGroupName && this.$refs.toGroupName) {
        this.$refs.fromGroupName.style.height = this.$refs.toGroupName.style.height = ''
        this.$refs.fromGroupName.style.height = this.$refs.toGroupName.style.height = Math.max(this.$refs.fromGroupName.offsetHeight, this.$refs.toGroupName.offsetHeight) + 'px'
      }
    },
    stopPropagation (e) {
      e.stopPropagation()
    }
  }
}
</script>

<style lang="scss" scoped>
.node-info-layer {
  position: absolute;
  left: 0;
  top: 0;
  width: 270px;
  margin: 5px 0 0 5px;
  padding: 15px 15px 0 15px;
  border-radius: 12px;
  box-shadow: 0 12px 15px -6px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  background-color: #fff;
  transform-origin: 0 0;
  z-index: 6;
  cursor: default;

  &-enter-active, &-leave-active {
    transition: opacity 0.1s, transform 0.1s $ease-out-quart;
  }
  &-enter, &-leave-to {
    opacity: 0;
    transform: translate3d(0, 3px, 0);
  }

  .info-item {
    float: left;
    width: 100px;
    font-size: 12px;
    color: #767676;
    line-height: 1.33;
    white-space: normal;
    word-break: break-all;

    .sequence {
      position: relative;
      height: 36px;
      font-size: 10px;
      font-weight: 500;
      line-height: 35px;
      padding: 0 0 0 40px;
      border-radius: 6px;
      border: solid 1px #d7d7d7;
      box-sizing: border-box;
      display: block;

      strong {
        position: absolute;
        left: 11px;
        top: 0;
        font-size: 20px;
        font-weight: bold;
        color: #555;
      }
    }

    .group-info {
      position: relative;
      padding: 10px 0 10px 18px;
      border-bottom: 1px dashed #d7d7d7;
      box-sizing: border-box;
      display: block;
      cursor: text;

      &:before {
        position: absolute;
        left: 0;
        top: 11px;
        width: 12px;
        height: 12px;
        content: '';
        border-radius: 100%;
        background-color: var(--group-color);
      }
    }

    .url {
      padding: 10px 0;
      display: block;
      cursor: text;
    }
  }

  .info-item + .info-item {
    position: relative;
    float: right;

    &:before {
      position: absolute;
      left: -30px;
      top: 8px;
      width: 20px;
      height: 20px;
      content: '';
      background: url('../../assets/img/trend/icon--arrow-right.svg') no-repeat;
    }
  }

  .summary {
    position: relative;
    clear: both;
    color: #0e90a5;
    margin: 10px -15px 0;
    padding: 16px 15px;
    border-top: 1px solid #ebebeb;

    .sessions {
      position: relative;
      font-size: 18px;
      font-weight: bold;
      line-height: 1;
      padding-left: 26px;
      display: block;

      .icon {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
      }

      .percent {
        font-size: 15px;
        line-height: 1;
      }
    }

    &.thru-path {
      padding: 9px 15px;

      .sessions {
        font-size: 15px;
        line-height: 1;

        .percent {
          font-size: 13px;
          line-height: 1;
        }

        .total {
          font-size: 12px;
          color: #89c6d0;
          line-height: 1;
          margin-top: 5px;
          display: block;

          .percent {
            font-size: 12px;
            line-height: 1;
          }
        }
      }
    }
  }
}
</style>
